import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  Row,
  Col,
  Tag,
  Tabs,
  Select,
  message,
  Input,
  Button,
} from "antd";
import { EditOutlined, CheckOutlined, CloseOutlined, FileTextOutlined, DollarOutlined, CheckCircleOutlined, UserOutlined, ArrowLeftOutlined } from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import dayjs from "dayjs";

import {
  fetchDealsApi,
  updateDealMetaApi,
  getDealActivitiesApi,
  addDealActivityApi,
  fetchCrmFieldsApi,
} from "../../../../apiservice/crm";
import { convertToCompanyTimezoneObject } from "../../../../lib/functions";
import { selectAuthInfo } from "../../../Auth/authSelectors";
import "../../crmSharedStyle.css";

const DealDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const authInfo = useSelector(selectAuthInfo);
  const currency = authInfo?.currency || "";

  const [deal, setDeal] = useState(null);
  const [editingStage, setEditingStage] = useState(false);
  const [tempStage, setTempStage] = useState(null);

  const [activities, setActivities] = useState([]);
  const [text, setText] = useState("");

  const [customFields, setCustomFields] = useState([]);

  const stageOptions = [
    { label: "Prospecting", value: "prospecting" },
    { label: "Negotiation", value: "negotiation" },
    { label: "Won", value: "won" },
    { label: "Lost", value: "lost" },
  ];

  const stageColor = {
    prospecting: "orange",
    negotiation: "blue",
    won: "green",
    lost: "red",
  };

  // 🔹 LOAD DEAL
  const load = async () => {
    const res = await fetchDealsApi({});
    const found = res.data.find((d) => d.deal_id === id);
    setDeal(found);
  };

  // 🔹 LOAD ACTIVITIES
  const loadActivities = async () => {
    const res = await getDealActivitiesApi({ deal_id: id });
    setActivities(res.data || []);
  };

  // 🔹 LOAD CUSTOM FIELDS
  useEffect(() => {
    const loadFields = async () => {
      const res = await fetchCrmFieldsApi({ module: "deal" });
      setCustomFields(res.data || []);
    };
    loadFields();
  }, []);

  useEffect(() => {
    load();
    loadActivities();
  }, [id]);

  // 🔹 UPDATE STAGE
  const saveStage = async () => {
    try {
      await updateDealMetaApi({
        deal_id: deal.deal_id,
        stage: tempStage,
      });

      setDeal((prev) => ({ ...prev, stage: tempStage }));
      setEditingStage(false);
      message.success("Stage updated");
    } catch {
      message.error("Update failed");
    }
  };

  // 🔹 ADD ACTIVITY
  const addActivity = async () => {
    if (!text.trim()) return;

    await addDealActivityApi({
      deal_id: deal.deal_id,
      text,
      type: "note",
    });

    setText("");
    loadActivities();
  };

  // 🔹 GROUP ACTIVITIES (timeline)
  const grouped = useMemo(() => {
    const map = {};
    activities.forEach((a) => {
      // const date = dayjs(a.created_at).format("DD MMM YYYY");
      const date = convertToCompanyTimezoneObject(a.created_at).format("DD MMM YYYY");
      if (!map[date]) map[date] = [];
      map[date].push(a);
    });
    return map;
  }, [activities]);

  const getActivityMeta = (a) => {
    if (a.source === "task") {
      return {
        icon: <CheckCircleOutlined />,
        color: "#fa8c16",
      };
    }

    if (a.type === "system") {
      return {
        icon: <UserOutlined />,
        color: "#13c2c2",
      };
    }

    if (a.source === "deal") {
      return {
        icon: <DollarOutlined />,
        color: "#722ed1",
      };
    }

    return {
      icon: <FileTextOutlined />,
      color: "#52c41a",
    };
  };

  if (!deal) return <p>Loading...</p>;

  return (
    <div className="p-4" style={{ maxWidth: 1200, margin: "0 auto" }}>

      {/* 🔥 HEADER */}
      <Card
        style={{
          marginBottom: 16,
          borderRadius: 12,
          boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
          border: "1px solid #f0f0f0",
        }}
        bodyStyle={{ padding: 16 }}
      >
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate(-1)}
          style={{ paddingLeft: 0, color: "#8c8c8c", height: "auto", marginBottom: 8 }}
        />
        <Row justify="space-between" gutter={[12, 12]}>
          <Col xs={24} sm={18}>
            <h2 style={{ margin: 0 }}>{deal.title}</h2>
            <p style={{ margin: 0, color: "#595959" }}>{deal.contact_name}</p>
          </Col>

          <Col xs={24} sm={6} style={{ textAlign: "left" }}>
            {!editingStage ? (
              <>
                <Tag
                  color={stageColor[deal.stage]}
                  onClick={() => {
                    setEditingStage(true);
                    setTempStage(deal.stage);
                  }}
                  style={{ cursor: "pointer" }}
                >
                  {deal.stage}
                </Tag>

                <EditOutlined
                  style={{ marginLeft: 8, cursor: "pointer" }}
                  onClick={() => {
                    setEditingStage(true);
                    setTempStage(deal.stage);
                  }}
                />
              </>
            ) : (
              <>
                <Select
                  value={tempStage}
                  onChange={setTempStage}
                  options={stageOptions}
                  style={{ width: 150 }}
                />

                <CheckOutlined
                  style={{ marginLeft: 8, color: "green" }}
                  onClick={saveStage}
                />

                <CloseOutlined
                  style={{ marginLeft: 8, color: "red" }}
                  onClick={() => setEditingStage(false)}
                />
              </>
            )}
          </Col>
        </Row>
      </Card>

      {/* 🔥 TABS */}
      <Tabs
        defaultActiveKey="info"
        items={[
          {
            key: "info",
            label: "Info",
            children: (
              <Card>
                <Row gutter={[16, 12]}>
                  <Col xs={24} sm={12} md={8}>
                    <b>Amount:</b> {currency} {Number(deal.value || 0).toLocaleString()}
                  </Col>

                  <Col xs={24} sm={12} md={8}>
                    <b>Close Date:</b>{" "}
                    {deal.expected_close_date
                      ? dayjs(deal.expected_close_date).format("DD MMM YYYY")
                      : "-"}
                  </Col>

                  <Col xs={24} sm={12} md={8}>
                    <b>Created:</b>{" "}
                    {
                    // dayjs(deal.created_at).format("DD MMM YYYY")
                    convertToCompanyTimezoneObject(deal.created_at).format("DD MMM YYYY")
                    }

                  </Col>

                  <Col xs={24} sm={12} md={8}>
                    <b>Last Activity:</b>{" "}
                    {deal.last_activity_at
                      // ? dayjs(deal.last_activity_at).format("DD MMM, hh:mm A")
                      ? convertToCompanyTimezoneObject(deal.last_activity_at).format("DD MMM, hh:mm A")
                      : "None"}
                  </Col>

                  <Col xs={24} sm={12} md={8}>
                    <b>Activities:</b> {deal.activity_count || 0}
                  </Col>
                </Row>

                {/* 🔥 CUSTOM FIELDS */}
                {customFields.length > 0 && (
                  <div style={{ marginTop: 20 }}>
                    <div className="crm-section-title">Additional Info</div>

                    <Row gutter={[16, 12]}>
                      {customFields.map((f) => (
                        <Col xs={24} sm={12} md={8} key={f.id}>
                          <b>{f.field_name}:</b>{" "}
                          {deal.custom_fields?.[f.field_key] || "-"}
                        </Col>
                      ))}
                    </Row>
                  </div>
                )}
              </Card>
            ),
          },

          {
            key: "activity",
            label: "Activities",
            children: (
              <>
                <Card
                  style={{
                    marginBottom: 10,
                    borderRadius: 10,
                  }}
                >
                  <Input.TextArea
                    rows={3}
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    placeholder="Add note..."
                  />

                  <Button
                    type="primary"
                    onClick={addActivity}
                    style={{ marginTop: 10 }}
                  >
                    Add Note
                  </Button>
                </Card>

                {activities.length ? (
                  <div className="timeline-wrapper">
                    {Object.entries(grouped).map(([date, items]) => (
                      <div key={date} className="timeline-group">

                        {/* DATE */}
                        <div className="timeline-date">
                          {date}
                        </div>

                        {items.map((a, i) => {
                          const meta = getActivityMeta(a);

                          return (
                            <div key={i} className="timeline-item">

                              {/* ICON */}
                              <div
                                className="timeline-icon"
                                style={{
                                  background: meta.color,
                                }}
                              >
                                {meta.icon}
                              </div>

                              {/* CONTENT */}
                              <div className="timeline-content">

                                <div className="timeline-text">
                                  {a.text}
                                </div>

                                <div className="timeline-time">
                                  {
                                  // dayjs(a.created_at).format(
                                  //   "hh:mm A"
                                  // )
                                  convertToCompanyTimezoneObject(a.created_at).format(
                                    "hh:mm A"
                                  )
                                  }
                                </div>

                                {a.follow_up_date && (
                                  <div className="timeline-meta">
                                    Follow-up:{" "}
                                    {
                                    // dayjs(
                                    //   a.follow_up_date
                                    // ).format("DD MMM")
                                    convertToCompanyTimezoneObject(
                                      a.follow_up_date
                                    ).format("DD MMM")
                                    }
                                  </div>
                                )}

                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                ) : (
                  <Card
                    style={{
                      textAlign: "center",
                      borderRadius: 12,
                    }}
                  >
                    No activity yet
                  </Card>
                )}
              </>
            ),
          }
        ]}
      />
    </div>
  );
};

export default DealDetails;